import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import PaperDraggable from "../../../../../../components/PaperDraggable";
import { Controller, useForm } from "react-hook-form";
import SelectTransportadora from "../../../../../../components/SelectsSimpleApiQuery/SelectTransportadora";
import { formatarInputDecimal, formatarValorMonetario, permitirApenasNumeros } from "../../../../../../helpers/masks";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { IDataDetalharPedido } from "../../../../../../types/api/comercial/detalhar-pedido";
import moment from "moment";
import { IEmitirNotaMutation } from "../../../../../../types/mutations/comercial/emitir-nota-fiscal";
import { IVerificarStatusNfe } from "../../../../../../types/api/estoque/verificar-status-nfe";

export default function DialogEmitirNotaFiscal({
  open,
  data,
  expedicao_id,
  handleClose
}: DialogEmitirNotaFiscalProps) {

  const queryClient = useQueryClient();

  const methods = useForm<TEmitirNotaFiscal>({
    defaultValues: {
      forma_entrega: data.frete > 0 ? "Pela Cooperativa" : "Sem frete",
      transportadora: null,
      qtd_volumes: "",
      peso_liquido: "",
      peso_bruto: "",
    }
  })
  const { handleSubmit, control, watch, setValue } = methods;

  //Querys e mutations
  const { mutate: emitir, isLoading } = useMutation({
    mutationKey: 'emitir-nota-fiscal',
    mutationFn: (inputs: IEmitirNotaMutation) => axiosClient.post(`comercial/pedidos/emitir-nota-fiscal`, inputs),
    onError: (error) => {
      console.error(error)
      enqueueSnackbar('Erro ao emitir nota fiscal!', { variant: 'error' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(`comercial/detalhar-pedido`)
      handleClose()
    }
  })

  useQuery({
    queryKey: [`verificar-status-nfe/${expedicao_id}`, expedicao_id],
    queryFn: () => {
      return axiosClient.get<IVerificarStatusNfe>(`/estoque/expedicao/verificar-status-nfe/${expedicao_id}`, {
        params: {
          origem_emissao: "pedido_venda"
        }
      });
    },
    keepPreviousData: false,
    refetchOnWindowFocus: true,
    cacheTime: 0,
    enabled: open,
    onSuccess: (data) => {
      if (!data.data.status_nf) {
        setValue('qtd_volumes', data.data.volume.toString());
        setValue('peso_bruto', data.data.peso_bruto.toString().replace('.', ','));
        setValue('peso_liquido', data.data.peso_liquido.toString().replace('.', ','));
        return;;
      }
    },
  });


  const formaEntrega = watch("forma_entrega")

  const possuiTransportadora = useMemo(() => {
    return formaEntrega === "Pela Cooperativa"
  }, [formaEntrega])

  const possuiForm = useMemo(() => {
    return formaEntrega === "Pelo Cooperado"
  }, [formaEntrega])

  const opcoesEntrega = useMemo(() => {
    if (data.frete > 0) {
      return [
        "Pela Cooperativa"
      ]
    } else {
      return [
        "Sem frete",
        "Pelo Cooperado"
      ]
    }
  }, [data.frete])

  const onSubmit = (data: TEmitirNotaFiscal) => {

    const converterDecimal = (valor: string): number | null => {
      if (!valor || valor.trim() === '') return null;
      const numero = Number(valor.replace(',', '.'));
      return isNaN(numero) ? null : numero;
    };

    const params: IEmitirNotaMutation = {
      expedicao_id,
      forma_entrega: data.forma_entrega,
      transportador_id: Number(data.transportadora?.id) || null,
      peso_bruto: converterDecimal(data.peso_bruto),
      peso_liquido: converterDecimal(data.peso_liquido),
      qtd_volumes: Number(data.qtd_volumes) || null,
      origem_emissao: "pedido_venda"
    }
    emitir(params)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
      maxWidth={data.frete > 0 ? "xs" : "sm"}
    >
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Emitir nota fiscal</DialogTitle>
      <DialogContent>
        {
          formaEntrega === "Pela Cooperativa" &&
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            Esse pedido possui um valor de {formatarValorMonetario(data.frete)} de frete com previsão de entrega em {moment(data.previsao_entrega).format('DD/MM/YYYY')}. Escolha abaixo o transportador para o transporte da mercadoria.
          </Alert>
        }

        <Grid container spacing={2}>
          {
            formaEntrega !== "Pela Cooperativa" &&
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Como você gostaria de realizar a entrega deste pedido?
              </Typography>

              <Controller
                control={control}
                name="forma_entrega"
                rules={{ required: "Campo obrigatório" }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <FormControl
                    error={invalid}
                    required
                  >
                    <RadioGroup
                      value={field.value}
                      name="formaEntrega"
                      row
                      onChange={(e) => {
                        setValue("transportadora", null)
                        field.onChange(e.target.value)
                      }}
                    >
                      {
                        opcoesEntrega.map((item, index) => (
                          <FormControlLabel key={index} value={item} label={item} control={<Radio />} />
                        ))
                      }
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>
          }

          {
            possuiTransportadora &&
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="transportadora"
                rules={{ required: "Campo obrigatório" }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <SelectTransportadora
                    value={field.value}
                    onChange={(e) => field.onChange(e)}
                  />
                )}
              />
            </Grid>
          }

          {
            possuiForm &&
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="qtd_volumes"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label="Quantidade de volumes"
                      value={field.value}
                      onChange={(e) => field.onChange(
                        permitirApenasNumeros(e.target.value))
                      }
                      error={invalid}
                      helperText={error ? "Campo obrigatório" : ""}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="peso_liquido"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <TextField
                      required
                      fullWidth
                      type="text"
                      label="Peso líquido (kg)"
                      value={field.value}
                      onChange={(e) => {
                        const valor = formatarInputDecimal(e.target.value, 3);
                        field.onChange(valor);
                      }}
                      error={invalid}
                      helperText={error ? "Campo obrigatório" : ""}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 12 }}>
                <Controller
                  control={control}
                  name="peso_bruto"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <TextField
                      required
                      fullWidth
                      label="Peso bruto (kg)"
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const valor = formatarInputDecimal(e.target.value, 3);
                        field.onChange(valor);
                      }}
                      error={invalid}
                      helperText={error ? "Campo obrigatório" : ""}
                    />
                  )}
                />
              </Grid>
            </>
          }
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={isLoading}
          onClick={handleClose}
        >
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {
            isLoading &&
            <CircularProgress size={15} sx={{ marginRight: 1 }} />
          }
          Emitir
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface DialogEmitirNotaFiscalProps {
  open: boolean;
  data: IDataDetalharPedido
  handleClose: () => void;
  expedicao_id: number
}

type TEmitirNotaFiscal = {
  forma_entrega: string;
  transportadora: { label: string, id: string, doc: string } | null;
  qtd_volumes: string,
  peso_liquido: string,
  peso_bruto: string,
}